/*
 * UserAskActionSheet — 底部弹层版本的 user_ask 渲染容器
 *
 * 之前 user_ask 在 chat 流里渲染成卡片 (ChatPanel.tsx:1361),
 * 重要决策被普通消息淹没.改造为底部独立层:
 *   - SSE 收到 xhey_user_ask → 写入 aiClerkUserAsk store + 触发本组件弹出
 *   - 视觉:底部 70% 宽居中 + 顶部圆角 30 + 50% 黑遮罩 (沿用 pencil 设计稿)
 *   - 内容:直接复用现有 <UserAskBlock> 组件 (不抽离题型组件,最小改动)
 *   - 提交后:onSubmit 回调上抛到 work/index.tsx 走原有响应链路
 *
 * scope: 所有样式在 .aiClerk-userAskSheet-* 前缀下, 不污染全局 antd class.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext
} from 'react'
import { createPortal } from 'react-dom'
import { useAppSelector, useAppDispatch } from '../../../app/hooks'
import {
  closeActionSheet,
  recordSubmittedAnswers
} from '../../../slices/aiClerkUserAskSlice'
import UserAskBlock from './blocks/UserAskBlock'
import type { MessagePart, UserAskQuestion } from '../types'
import { trackClick, readSessionId } from '../utils/track'
import {
  truncateTitle,
  buildTypeCombo,
  hasAnyDefault,
  buildAnswerSummary,
  countChangedQuestions,
  allQuestionsChanged
} from '../utils/userAskTrack'
import './UserAskActionSheet.scss'

/** 协议: ai_form_filler_user_ask_tracking.md ask_* 系列 track_sign 固定. */
const ASK_TRACK_SIGN = 'aai.b8137.click.ai_click_web_ai_form_filling'

type PickerKind = 'members' | 'watermarks'

/**
 * 内部 picker (DepartmentMemberPickerV2 / WatermarksPicker) 打开/关闭状态上报通道.
 * ActionSheet 内 render UserAskBlock 时提供 Context, PickerField 内部 useEffect
 * 监听 visible 变化 → setPickerOpen(visible).
 *
 * 用途: picker 打开时 ActionSheet 隐藏自己,避免多层弹窗叠加视觉混乱.
 * chat 流 inline 模式 (legacy displayAs='inline') Context 不存在 (value=null),
 * UserAskBlock 跳过上报,保持原有行为.
 */
export const ActionSheetPickerContext = createContext<{
  setPickerOpen: (open: boolean) => void
  /** PickerField 上报埋点用 (open/close + confirm/cancel + 停留时长).
   *  inline 模式 Context 为 null,PickerField 跳过埋点 (chat 流不属于 ask_picker_* 场景). */
  reportPickerOpen: (kind: PickerKind) => void
  reportPickerClose: (kind: PickerKind, reason: 'confirm' | 'cancel') => void
  /** 单题跳过上报 — UserAskBlock toggleSkip 启用跳过时调用 */
  reportAskSkip: () => void
} | null>(null)

interface UserAskActionSheetProps {
  /** 跟现有 ChatPanel 的 onUserAskSubmit 同款签名,直接透传 work/index.tsx 的 handler */
  onSubmit: (response: {
    pending_id: string
    answers: Record<string, any>
    /** 右上角关闭/点遮罩 = 取消本轮（非正常提交），由 work 层换成「用户取消」上下文 */
    cancelled?: boolean
  }) => void
}

const UserAskActionSheet: React.FC<UserAskActionSheetProps> = ({
  onSubmit
}) => {
  const dispatch = useAppDispatch()
  const open = useAppSelector((s: any) => s.aiClerkUserAsk.open as boolean)
  const active = useAppSelector(
    (s: any) =>
      s.aiClerkUserAsk.active as {
        pendingId: string
        title: string
        description?: string
        questions: UserAskQuestion[]
        autoFillPreview?: Array<{ label: string; value: string }>
      } | null
  )
  // 已提交答案(按 pendingId)。重开已提交的 ask 时据此渲染只读摘要,而非编辑态。
  const submittedByPid = useAppSelector(
    (s: any) =>
      s.aiClerkUserAsk.submittedByPid as Record<string, Record<string, any>>
  )
  // ask 序号 (本会话第 N 个 ask) - 由 slice 维护,文档 ask_show.number2
  const askIndex = useAppSelector(
    (s: any) =>
      (active &&
        (s.aiClerkUserAsk.askIndexByPid as Record<string, number>)[
          active.pendingId
        ]) ||
      0
  )

  // 内部 picker (DepartmentMemberPickerV2 / WatermarksPicker) 是否打开.
  // picker 打开时本 ActionSheet 隐藏 (CSS display:none),让用户聚焦 picker;
  // picker 关闭后自动恢复显示, UserAskBlock 内部 state 保留(mount 不变).
  const [pickerOpen, setPickerOpen] = useState(false)

  // 埋点 — 每个 ask 的 show 时间戳 + 首次交互上报标记 + picker 停留计时.
  // 全部用 ref:跨渲染稳定且不触发 re-render;按 pendingId 索引避免多个 ask 串扰.
  const showTsRef = useRef<Record<string, number>>({})
  const firstInteractedRef = useRef<Record<string, boolean>>({})
  const pickerOpenTsRef = useRef(0)

  /** 算 "距 ask_show 的毫秒数",拿不到 showTs 兜底 0 (避免负数 / NaN) */
  const elapsedSinceShow = useCallback((pid: string): number => {
    const t0 = showTsRef.current[pid]
    if (!t0) return 0
    return Math.max(0, Date.now() - t0)
  }, [])

  // ask 弹出:open false→true 上报 ask_show,重置 first_interaction flag + 记 showTs
  useEffect(() => {
    if (!open || !active) return
    const pid = active.pendingId || ''
    // 同 pid 重开 (用户从 chip 重新打开历史 ask):showTs 已记过,不重计
    if (!showTsRef.current[pid]) {
      showTsRef.current[pid] = Date.now()
      firstInteractedRef.current[pid] = false
    }
    trackClick('formFilling', 'ask_show', {
      requirement_id: 1103,
      track_sign: ASK_TRACK_SIGN,
      sessionID: readSessionId(),
      string1: pid,
      string2: buildTypeCombo(active.questions || []),
      string3: truncateTitle(active.title),
      number1: (active.questions || []).length,
      number2: askIndex,
      bool1: hasAnyDefault(active.questions || []),
      bool2: !!(active.autoFillPreview && active.autoFillPreview.length > 0)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active?.pendingId])

  /** 首次任意交互上报 (focus/mousedown/change/scroll),同 ask 仅 1 次 */
  const reportFirstInteraction = useCallback(
    (
      pid: string,
      kind: 'focus' | 'change' | 'picker_open' | 'scroll'
    ): void => {
      if (!pid || firstInteractedRef.current[pid]) return
      firstInteractedRef.current[pid] = true
      trackClick('formFilling', 'ask_first_interaction', {
        requirement_id: 1103,
        track_sign: ASK_TRACK_SIGN,
        sessionID: readSessionId(),
        string1: pid,
        string2: kind,
        number1: elapsedSinceShow(pid)
      })
    },
    [elapsedSinceShow]
  )

  /** 把任意 DOM 事件兜成 first_interaction (mousedown/focus/change 任一即触发) */
  const handleInteractionEvent = useCallback(
    (e: React.SyntheticEvent) => {
      if (!active) return
      const t = e.type
      if (t === 'change') {
        reportFirstInteraction(active.pendingId, 'change')
      } else if (t === 'focusin' || t === 'focus') {
        reportFirstInteraction(active.pendingId, 'focus')
      } else if (t === 'mousedown') {
        reportFirstInteraction(active.pendingId, 'focus')
      } else if (t === 'scroll') {
        reportFirstInteraction(active.pendingId, 'scroll')
      }
    },
    [active, reportFirstInteraction]
  )

  /** picker 埋点上报通道,给 ActionSheetPickerContext 用 */
  const reportPickerOpen = useCallback(
    (kind: PickerKind) => {
      if (!active) return
      pickerOpenTsRef.current = Date.now()
      // picker 打开本身就是一种 first_interaction
      reportFirstInteraction(active.pendingId, 'picker_open')
      trackClick('formFilling', 'ask_picker_open', {
        requirement_id: 1103,
        track_sign: ASK_TRACK_SIGN,
        sessionID: readSessionId(),
        string1: active.pendingId,
        string2: kind
      })
    },
    [active, reportFirstInteraction]
  )

  const reportPickerClose = useCallback(
    (kind: PickerKind, reason: 'confirm' | 'cancel') => {
      if (!active) return
      const stayMs = pickerOpenTsRef.current
        ? Math.max(0, Date.now() - pickerOpenTsRef.current)
        : 0
      pickerOpenTsRef.current = 0
      trackClick('formFilling', 'ask_picker_close', {
        requirement_id: 1103,
        track_sign: ASK_TRACK_SIGN,
        sessionID: readSessionId(),
        string1: active.pendingId,
        string2: kind,
        string3: reason,
        number1: stayMs
      })
    },
    [active]
  )

  const reportAskSkip = useCallback(() => {
    if (!active) return
    trackClick('formFilling', 'ask_skip', {
      requirement_id: 1103,
      track_sign: ASK_TRACK_SIGN,
      sessionID: readSessionId(),
      string1: active.pendingId,
      number1: elapsedSinceShow(active.pendingId)
    })
  }, [active, elapsedSinceShow])

  const pickerCtxValue = useMemo(
    () => ({
      setPickerOpen,
      reportPickerOpen,
      reportPickerClose,
      reportAskSkip
    }),
    [reportPickerOpen, reportPickerClose, reportAskSkip]
  )

  const handleSubmit = useCallback(
    (response: { pending_id: string; answers: Record<string, any> }) => {
      // ask_submit 埋点 — 带答案摘要/决策耗时/改动情况 (文档 ask_submit)
      // 同一 ask 重提交不应重计,但目前流程不会 (submitted 后 chip 重开只读),先不做去重.
      if (active) {
        trackClick('formFilling', 'ask_submit', {
          requirement_id: 1103,
          track_sign: ASK_TRACK_SIGN,
          sessionID: readSessionId(),
          string1: response.pending_id,
          string2: buildAnswerSummary(active.questions || [], response.answers),
          number1: elapsedSinceShow(response.pending_id),
          number2: countChangedQuestions(
            active.questions || [],
            response.answers
          ),
          bool1: allQuestionsChanged(active.questions || [], response.answers)
        })
      }
      // 记录已提交答案 → 之后重开本 ask 渲染只读摘要(不再出现编辑态/提交按钮)
      dispatch(
        recordSubmittedAnswers({
          pendingId: response.pending_id,
          answers: response.answers
        })
      )
      onSubmit(response)
      dispatch(closeActionSheet())
    },
    [active, onSubmit, dispatch, elapsedSinceShow]
  )

  const handleClose = useCallback(() => {
    // 已提交的 ask 是「只读回看」，关闭 = 单纯关闭，不回传取消。
    // 未提交才视为取消本轮：回传空答案 + cancelled 标记，让 work 层拼出「用户取消」
    // 上下文发给 LLM；否则那条 user_ask 工具调用会一直挂着、模型也可能把"没响应"
    // 误当默认/全部继续往下猜。
    const alreadySubmitted = !!(active && submittedByPid[active.pendingId])
    if (active) {
      trackClick('formFilling', 'ask_close', {
        requirement_id: 1103,
        track_sign: ASK_TRACK_SIGN,
        sessionID: readSessionId(),
        string1: active.pendingId,
        bool1: alreadySubmitted,
        number1: elapsedSinceShow(active.pendingId)
      })
    }
    if (active && !alreadySubmitted) {
      onSubmit({ pending_id: active.pendingId, answers: {}, cancelled: true })
    }
    dispatch(closeActionSheet())
  }, [active, submittedByPid, onSubmit, dispatch, elapsedSinceShow])

  if (!open || !active) return null

  // 构造 UserAskBlock 需要的 part 形态.
  // 用 active store 数据 → MessagePart.user-ask 形态映射,复用现有组件
  const part: Extract<MessagePart, { type: 'user-ask' }> = {
    type: 'user-ask',
    pendingId: active.pendingId,
    title: active.title,
    description: active.description,
    questions: active.questions,
    autoFillPreview: active.autoFillPreview
  }

  // 用 React Portal 渲染到 document.body 直接子节点 — 避开 aiClerk-work 父容器
  // 创建的 stacking context (chat panel chip / sticky 元素会污染层级,导致
  // ActionSheet 即使 z-index 9999 也压不住).
  // Portal 后 z-index 用 1000 (跟 antd Modal 默认同级):
  //   - chat 流 chip 在 aiClerk-work 内,DOM 顺序早于 ActionSheet → 被 ActionSheet 盖住 ✓
  //   - 用户在 UserAskBlock 内点 picker → antd Modal 后渲染到 body,同 z-index
  //     时 DOM 后插入的在上层 → picker Modal 自然在 ActionSheet 上面 ✓
  // picker 打开时整组 (mask + sheet) 加 --hidden 修饰符 → CSS display:none,
  // 仍 mount 保留 UserAskBlock state.picker 在 body 上独立显示(由
  // UserAskBlock 内 createPortal / antd Modal 自管),不受本组件 hidden 影响.
  const hiddenCls = pickerOpen ? ' aiClerk-userAskSheet--hidden' : ''

  return createPortal(
    <ActionSheetPickerContext.Provider value={pickerCtxValue}>
      {/* 遮罩仅压暗背景：点遮罩不关弹窗(弹窗悬浮，只有 ✕ / 提交 能关) */}
      <div
        className={`aiClerk-userAskSheet-mask${hiddenCls}`}
        aria-hidden="true"
      />
      <div
        className={`aiClerk-userAskSheet${hiddenCls}`}
        role="dialog"
        aria-modal="true"
        onMouseDownCapture={handleInteractionEvent}
        onFocusCapture={handleInteractionEvent}
        onChangeCapture={handleInteractionEvent}
        onScrollCapture={handleInteractionEvent}
      >
        <button
          type="button"
          className="aiClerk-userAskSheet__close"
          onClick={handleClose}
          aria-label="关闭"
        >
          ✕
        </button>
        <div className="aiClerk-userAskSheet__body">
          <UserAskBlock
            part={part}
            isLastMessage={true}
            onSubmit={handleSubmit}
            presentation="step"
            submittedAnswers={submittedByPid[active.pendingId]}
          />
        </div>
      </div>
    </ActionSheetPickerContext.Provider>,
    document.body
  )
}

export default UserAskActionSheet
